import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import PrintPreview from './PrintPreview';
import { fmt } from '../../utils/calc';
import { TAUX } from '../../data/tarifsData';
import './ArchivesModal.css';

// ── Statut config ─────────────────────────────────────────
const STATUTS = [
  { val: 'nouveau',   label: 'Nouveau',   color: '#00BFFF', bg: 'rgba(0,191,255,0.1)'  },
  { val: 'en_cours',  label: 'En cours',  color: '#FFB800', bg: 'rgba(255,184,0,0.12)' },
  { val: 'payé',      label: 'Payé',      color: '#00FF88', bg: 'rgba(0,255,136,0.1)'  },
  { val: 'annulé',    label: 'Annulé',    color: '#FF4060', bg: 'rgba(255,64,96,0.1)'  },
];

function statutStyle(val) {
  const s = STATUTS.find(s => s.val === val) || STATUTS[0];
  return { color: s.color, background: s.bg, border: `1px solid ${s.color}40` };
}
function statutLabel(val) {
  return (STATUTS.find(s => s.val === val) || STATUTS[0]).label;
}

// ── Single row in the list ────────────────────────────────
function DocRow({ doc, isAdmin, isManager, onSelect, onStatut, onDelete, onFolder }) {
  const isF = doc.type === 'facture';
  return (
    <div className={'arc-row' + (isF ? ' arc-row-fac' : ' arc-row-dev')}>
      <div className="arc-row-type">
        <span className={'arc-badge ' + (isF ? 'badge-fac' : 'badge-dev')}>
          {isF ? '🧾 FAC' : '📋 DEV'}
        </span>
      </div>
      <div className="arc-row-main">
        <div className="arc-row-num">
          <strong>{doc.num}</strong>
          {doc.classeFolder && <span className="arc-folder-tag">📁 {doc.classeFolder}</span>}
        </div>
        <div className="arc-row-client">👤 {doc.client || '—'}</div>
        <div className="arc-row-meta">
          <span>🗓 {doc.dateStr}</span>
          <span>✍️ {doc.worker}</span>
          {doc.workerRole && <span className="arc-role-tag">{doc.workerRole}</span>}
        </div>
        {doc.adminNote && (
          <div className="arc-admin-note">📌 {doc.adminNote}</div>
        )}
      </div>
      <div className="arc-row-amounts">
        <div className="arc-total">{fmt(doc.total)}</div>
        <div className="arc-sub-amt">
          ≈ {Math.round(doc.total / TAUX.USD_TO_FCFA).toLocaleString('fr-FR')} $
          &nbsp;|&nbsp;
          {Math.round(doc.total / TAUX.EUR_TO_FCFA).toLocaleString('fr-FR')} €
        </div>
        {doc.acompte > 0 && (
          <div className="arc-restant">
            Reste : <strong>{fmt(doc.restant)}</strong>
          </div>
        )}
      </div>
      <div className="arc-row-statut">
        {(isAdmin || isManager) ? (
          <select
            className="arc-statut-sel"
            style={statutStyle(doc.statut)}
            value={doc.statut}
            onChange={e => onStatut(doc.id, e.target.value)}
            disabled={!isAdmin}
            title={!isAdmin ? 'Seul l\'admin peut modifier le statut' : ''}
          >
            {STATUTS.map(s => (
              <option key={s.val} value={s.val}>{s.label}</option>
            ))}
          </select>
        ) : (
          <span className="arc-statut-badge" style={statutStyle(doc.statut)}>
            {statutLabel(doc.statut)}
          </span>
        )}
      </div>
      <div className="arc-row-actions">
        <button className="arc-btn arc-btn-view" onClick={() => onSelect(doc)} title="Voir le document">
          👁
        </button>
        {isAdmin && (
          <button className="arc-btn arc-btn-folder" onClick={() => onFolder(doc)} title="Classer dans un dossier">
            📁
          </button>
        )}
        {isAdmin && (
          <button className="arc-btn arc-btn-del" onClick={() => onDelete(doc.id)} title="Supprimer (admin uniquement)">
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────
export default function ArchivesModal({ onClose }) {
  const { data, currentUser, deleteTransaction, updateTransaction, showToast } = useApp();

  const isAdmin   = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';

  // ── Filters state ──────────────────────────────────────
  const [search,     setSearch]     = useState('');
  const [filterType, setFilterType] = useState('all');   // 'all' | 'facture' | 'devis'
  const [filterStat, setFilterStat] = useState('all');   // 'all' | statut val
  const [filterWorker, setFilterWorker] = useState('all');
  const [filterFolder, setFilterFolder] = useState('all');
  const [sortBy,     setSortBy]     = useState('date_desc');

  // ── Detail view ────────────────────────────────────────
  const [selected, setSelected] = useState(null);        // full doc to show in PrintPreview

  // ── Note editor ────────────────────────────────────────
  const [editNote, setEditNote] = useState(null);        // { id, note }

  // ── Folder dialog ──────────────────────────────────────
  const [folderDoc, setFolderDoc] = useState(null);
  const [folderVal, setFolderVal] = useState('');

  // ── All unique workers & folders for filters ──────────
  const allWorkers = useMemo(() => {
    const w = new Set(data.transactions.map(t => t.worker).filter(Boolean));
    return Array.from(w);
  }, [data.transactions]);

  const allFolders = useMemo(() => {
    const f = new Set(data.transactions.map(t => t.classeFolder).filter(Boolean));
    return Array.from(f);
  }, [data.transactions]);

  // ── Filtered + sorted list ─────────────────────────────
  const docs = useMemo(() => {
    let list = [...data.transactions];

    if (filterType !== 'all') list = list.filter(t => t.type === filterType);
    if (filterStat !== 'all') list = list.filter(t => t.statut === filterStat);
    if (filterWorker !== 'all') list = list.filter(t => t.worker === filterWorker);
    if (filterFolder !== 'all') {
      if (filterFolder === '__none__') list = list.filter(t => !t.classeFolder);
      else list = list.filter(t => t.classeFolder === filterFolder);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        (t.num        || '').toLowerCase().includes(q) ||
        (t.client     || '').toLowerCase().includes(q) ||
        (t.worker     || '').toLowerCase().includes(q) ||
        (t.classeFolder||'').toLowerCase().includes(q) ||
        (t.adminNote  || '').toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':    return a.id - b.id;
        case 'date_desc':   return b.id - a.id;
        case 'total_desc':  return b.total - a.total;
        case 'total_asc':   return a.total - b.total;
        case 'client':      return (a.client||'').localeCompare(b.client||'');
        case 'statut':      return (a.statut||'').localeCompare(b.statut||'');
        default:            return b.id - a.id;
      }
    });

    return list;
  }, [data.transactions, filterType, filterStat, filterWorker, filterFolder, search, sortBy]);

  // ── KPIs ───────────────────────────────────────────────
  const kpis = useMemo(() => {
    const all = data.transactions;
    return {
      total:   all.length,
      fac:     all.filter(t => t.type === 'facture').length,
      dev:     all.filter(t => t.type === 'devis').length,
      ca:      all.filter(t => t.type === 'facture').reduce((s, t) => s + t.total, 0),
      encaisse:all.filter(t => t.statut === 'payé').reduce((s, t) => s + t.total, 0),
      attente: all.filter(t => t.statut === 'en_cours' || t.statut === 'nouveau').reduce((s, t) => s + t.restant, 0),
    };
  }, [data.transactions]);

  // ── Actions ────────────────────────────────────────────
  function handleDelete(id) {
    if (!isAdmin) return;
    if (!window.confirm('Supprimer définitivement ce document ? Cette action est irréversible.')) return;
    deleteTransaction(id);
    showToast('Document supprimé.', 'warn');
    if (selected?.id === id) setSelected(null);
  }

  function handleStatut(id, val) {
    if (!isAdmin) return;
    updateTransaction(id, { statut: val });
    showToast('Statut mis à jour.');
  }

  function handleSaveNote() {
    if (!editNote) return;
    updateTransaction(editNote.id, { adminNote: editNote.note });
    setEditNote(null);
    showToast('Note enregistrée.');
  }

  function handleSaveFolder() {
    if (!folderDoc) return;
    updateTransaction(folderDoc.id, { classeFolder: folderVal.trim() });
    setFolderDoc(null);
    setFolderVal('');
    showToast('Classé dans : ' + (folderVal.trim() || '(sans dossier)'));
  }

  // ── Access guard (after all hooks) ────────────────────
  if (!isAdmin && !isManager) return null;

  // ── Detail panel for PrintPreview ──────────────────────
  if (selected) {
    const totals = {
      sub: selected.sub, total: selected.total,
      remisePct: selected.remisePct, remiseAmt: selected.remiseAmt,
      tvaPct: selected.tvaPct, tvaAmt: selected.tvaAmt,
      acompte: selected.acompte, restant: selected.restant,
    };
    return (
      <div className="overlay open">
        <div className="modal modal-xl">
          <div className="mhead">
            <div>
              <h2>📄 {selected.type === 'facture' ? '🧾 Facture' : '📋 Devis'} — {selected.num}</h2>
              <p style={{ fontSize:13, color:'var(--muted)', marginTop:3 }}>
                👤 {selected.client} · ✍️ {selected.worker} · 🗓 {selected.dateStr}
              </p>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              {isAdmin && (
                <button
                  className="abtn abtn-muted"
                  style={{ fontSize:12 }}
                  onClick={() => setEditNote({ id: selected.id, note: selected.adminNote || '' })}
                >
                  📌 Note admin
                </button>
              )}
              <button className="abtn abtn-muted" style={{ fontSize:12 }} onClick={() => setSelected(null)}>
                ← Retour aux archives
              </button>
              <button className="mclose" onClick={onClose}>✕</button>
            </div>
          </div>
          <div className="mbody">
            {/* Admin note editor inline */}
            {editNote && editNote.id === selected.id && (
              <div className="arc-note-editor">
                <label>📌 Note interne (visible uniquement par l'admin)</label>
                <textarea
                  rows={2}
                  value={editNote.note}
                  onChange={e => setEditNote(n => ({ ...n, note: e.target.value }))}
                  placeholder="Note de suivi, instruction, remarque…"
                />
                <div style={{ display:'flex', gap:8, marginTop:6 }}>
                  <button className="abtn abtn-green" style={{ fontSize:12 }} onClick={handleSaveNote}>✓ Enregistrer</button>
                  <button className="abtn abtn-muted" style={{ fontSize:12 }} onClick={() => setEditNote(null)}>Annuler</button>
                </div>
              </div>
            )}
            <PrintPreview
              type={selected.type}
              meta={{
                num:    selected.num,    date:   selected.date,
                valid:  selected.valid,  cnom:   selected.cnom,
                ctel:   selected.ctel,   cemail: selected.cemail,
                cadr:   selected.cadr,   notes:  selected.notes,
                worker: selected.worker,
              }}
              lines={selected.lines || []}
              totals={totals}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay open">
      <div className="modal modal-xl">
        {/* Header */}
        <div className="mhead">
          <div>
            <h2>🗄️ Archives — Factures & Devis</h2>
            <p style={{ fontSize:13, color:'var(--muted)', marginTop:3 }}>
              {isAdmin ? '🔐 Administrateur — consultation, classement et suppression' : '📋 Manager — consultation et suivi'}
            </p>
          </div>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>

        {/* KPIs */}
        <div className="arc-kpis">
          <div className="arc-kpi">
            <div className="arc-kpi-val">{kpis.total}</div>
            <div className="arc-kpi-lbl">Documents</div>
          </div>
          <div className="arc-kpi">
            <div className="arc-kpi-val" style={{ color:'#00BFFF' }}>{kpis.fac}</div>
            <div className="arc-kpi-lbl">Factures</div>
          </div>
          <div className="arc-kpi">
            <div className="arc-kpi-val" style={{ color:'#F5C842' }}>{kpis.dev}</div>
            <div className="arc-kpi-lbl">Devis</div>
          </div>
          <div className="arc-kpi">
            <div className="arc-kpi-val" style={{ color:'#00FF88' }}>{fmt(kpis.ca)}</div>
            <div className="arc-kpi-lbl">CA Facturé</div>
          </div>
          <div className="arc-kpi">
            <div className="arc-kpi-val" style={{ color:'#00FF88' }}>{fmt(kpis.encaisse)}</div>
            <div className="arc-kpi-lbl">Encaissé</div>
          </div>
          <div className="arc-kpi">
            <div className="arc-kpi-val" style={{ color:'#FFB800' }}>{fmt(kpis.attente)}</div>
            <div className="arc-kpi-lbl">En attente</div>
          </div>
        </div>

        {/* Filters */}
        <div className="arc-filters">
          <input
            className="arc-search"
            placeholder="🔍 N° facture, client, ouvrier, dossier…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">📄 Tous types</option>
            <option value="facture">🧾 Factures</option>
            <option value="devis">📋 Devis</option>
          </select>
          <select value={filterStat} onChange={e => setFilterStat(e.target.value)}>
            <option value="all">🔘 Tous statuts</option>
            {STATUTS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
          </select>
          <select value={filterWorker} onChange={e => setFilterWorker(e.target.value)}>
            <option value="all">👤 Tous émetteurs</option>
            {allWorkers.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
          <select value={filterFolder} onChange={e => setFilterFolder(e.target.value)}>
            <option value="all">📁 Tous dossiers</option>
            <option value="__none__">📂 Sans dossier</option>
            {allFolders.map(f => <option key={f} value={f}>📁 {f}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="date_desc">↓ Date (récent)</option>
            <option value="date_asc">↑ Date (ancien)</option>
            <option value="total_desc">↓ Montant</option>
            <option value="total_asc">↑ Montant</option>
            <option value="client">A-Z Client</option>
            <option value="statut">Statut</option>
          </select>
        </div>

        {/* Results count */}
        <div className="arc-results-bar">
          {docs.length === 0
            ? 'Aucun document trouvé'
            : `${docs.length} document(s) · Total filtré : ${fmt(docs.reduce((s, t) => s + t.total, 0))}`}
          {!isAdmin && (
            <span className="arc-readonly-hint">👁 Lecture seule — la suppression est réservée à l'administrateur</span>
          )}
        </div>

        {/* List */}
        <div className="arc-list">
          {docs.length === 0 ? (
            <div className="arc-empty">
              <div style={{ fontSize:52 }}>🗄️</div>
              <p>Aucune facture ni devis enregistré</p>
              <p className="arc-empty-hint">
                Les documents sont automatiquement archivés lors de leur enregistrement depuis les modals Facture et Devis.
              </p>
            </div>
          ) : (
            docs.map(doc => (
              <DocRow
                key={doc.id}
                doc={doc}
                isAdmin={isAdmin}
                isManager={isManager}
                onSelect={setSelected}
                onStatut={handleStatut}
                onDelete={handleDelete}
                onFolder={d => { setFolderDoc(d); setFolderVal(d.classeFolder || ''); }}
              />
            ))
          )}
        </div>

        {/* Folder dialog */}
        {folderDoc && (
          <div className="arc-dialog-overlay" onClick={() => setFolderDoc(null)}>
            <div className="arc-dialog" onClick={e => e.stopPropagation()}>
              <h4>📁 Classer le document</h4>
              <p style={{ fontSize:13, color:'var(--muted)', marginBottom:12 }}>
                {folderDoc.num} — {folderDoc.client}
              </p>
              <label style={{ fontSize:12, color:'var(--muted)', display:'block', marginBottom:6 }}>
                Nom du dossier (laisser vide pour déclasser)
              </label>
              <input
                className="arc-dialog-input"
                value={folderVal}
                onChange={e => setFolderVal(e.target.value)}
                placeholder="Ex: Clients 2025, Projets web, En attente…"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleSaveFolder()}
              />
              {allFolders.length > 0 && (
                <div className="arc-folder-suggestions">
                  <span style={{ fontSize:11, color:'var(--muted)' }}>Dossiers existants :</span>
                  {allFolders.map(f => (
                    <button key={f} className="arc-folder-sug" onClick={() => setFolderVal(f)}>
                      📁 {f}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ display:'flex', gap:8, marginTop:14 }}>
                <button className="abtn abtn-green" onClick={handleSaveFolder}>✓ Classer</button>
                <button className="abtn abtn-muted" onClick={() => setFolderDoc(null)}>Annuler</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
